// In-page viewer for one Consumer Voice audit, fetched by endpoint (the list
// rows only carry ids). Download builds the PDF client-side.
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FileDownloadOutlined } from '@mui/icons-material';
import axiosInstant from '../../api';
import { ConsumerVoiceAudit } from '../../types/consumerVoice';
import { ConsumerVoiceReportView } from './ConsumerVoiceReportView';
import { buildConsumerVoicePdfBlob, consumerVoiceFileName } from './consumerVoiceFiles';
import { downloadBlob } from '../PdpAudit/reportFiles';

export function ConsumerVoiceReportDialog({
  endpoint,
  onClose,
  onCreateStrategy,
  createStrategyLabel,
  productEndpoint,
}: {
  // GET endpoint returning the audit WITH its report.
  endpoint: string;
  onClose: () => void;
  onCreateStrategy?: (audit: ConsumerVoiceAudit) => void;
  createStrategyLabel?: string;
  // For brand reports: resolves a child product audit id to its GET
  // endpoint so the per-product rows open in place.
  productEndpoint?: (auditId: number) => string;
}) {
  const [current, setCurrent] = useState<string>(endpoint);
  const [audit, setAudit] = useState<ConsumerVoiceAudit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAudit(null);
    setError(null);
    axiosInstant
      .get(current)
      .then((res) => {
        if (!cancelled) setAudit(res.data as ConsumerVoiceAudit);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this analysis. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [current]);

  const download = async () => {
    if (!audit || !audit.report || downloading) return;
    setDownloading(true);
    try {
      downloadBlob(await buildConsumerVoicePdfBlob(audit), consumerVoiceFileName(audit));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'flex', gap: 0.5 }}>
        {current !== endpoint && (
          <button
            type="button"
            onClick={() => setCurrent(endpoint)}
            className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-surface hover:bg-surface-2"
          >
            ← Brand report
          </button>
        )}
        <Tooltip title="Download PDF">
          <span>
            <IconButton onClick={download} disabled={!audit || !audit.report || downloading} aria-label="Download PDF">
              <FileDownloadOutlined />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton onClick={onClose} aria-label="Close report">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        {!audit && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={26} />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 10 }}>
            {error}
          </Typography>
        )}
        {audit && (
          <ConsumerVoiceReportView
            audit={audit}
            onCreateStrategy={onCreateStrategy ? () => onCreateStrategy(audit) : undefined}
            createStrategyLabel={createStrategyLabel}
            onOpenProduct={productEndpoint ? (id) => setCurrent(productEndpoint(id)) : undefined}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
